// v3.15-1053: Quick-contact hover for Deal Ownership fields.
// Shows email / phone in a lightweight popover. NO network calls — uses
// already-loaded user datasets. Only displays for ASSIGNED users; the
// "Unassigned" state intentionally renders as a plain pass-through.
//
// Desktop: hover (HoverCard).
// Mobile: tap (PopoverPrimitive — radix HoverCard collapses to focus/click
//         on touch devices so a single component covers both).
//
// Strict scope: UI-only. No permission, payroll, routing, or commission
// implications. The wrapper does not alter the underlying Select trigger
// or its onValueChange handlers.
import React from 'react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from './ui/hover-card';
import { Mail, Phone } from 'lucide-react';

/**
 * @param {object}  props
 * @param {object}  props.user       — { full_name, email, phone, role }  (optional fields)
 * @param {string}  props.label      — fallback label when user is not assigned (e.g. "Unassigned")
 * @param {React.ReactNode} props.children — the trigger (typically the SelectTrigger)
 * @param {string}  [props.testid]   — optional data-testid prefix for the hover card
 */
export const OwnershipContactHover = ({ user, label, children, testid }) => {
  const hasUser   = !!(user && (user.email || user.phone));
  const fullName  = user?.full_name || label || '';
  const cleanLabel = String(label || '').trim().toLowerCase();
  const isUnassigned = cleanLabel === '' || cleanLabel === 'unassigned';

  // If there's no resolvable contact info OR the slot is unassigned, render
  // the trigger as a plain pass-through (no tooltip wrapper at all).
  if (!hasUser || isUnassigned) return children;

  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <div data-testid={testid ? `${testid}-trigger` : undefined}>{children}</div>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        sideOffset={6}
        className="w-64 p-3 text-xs"
        data-testid={testid ? `${testid}-card` : undefined}
      >
        <div className="font-semibold text-slate-800 truncate" data-testid={testid ? `${testid}-name` : undefined}>
          {fullName}
        </div>
        {user.role && (
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-0.5">
            {String(user.role).replace(/_/g, ' ')}
          </div>
        )}
        {user.email && (
          <a
            href={`mailto:${user.email}`}
            className="mt-2 flex items-center gap-1.5 text-blue-600 hover:underline truncate"
            data-testid={testid ? `${testid}-email` : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <Mail className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{user.email}</span>
          </a>
        )}
        {user.phone && (
          <a
            href={`tel:${String(user.phone).replace(/[^\d+]/g, '')}`}
            className="mt-1 flex items-center gap-1.5 text-blue-600 hover:underline"
            data-testid={testid ? `${testid}-phone` : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="h-3 w-3 flex-shrink-0" />
            <span>{user.phone}</span>
          </a>
        )}
        {!user.email && !user.phone && (
          <div className="mt-1 text-[11px] text-slate-400">No contact info on file.</div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default OwnershipContactHover;
